/**
 * Wraps the Django auth endpoints (/api/auth/register/, /login/) so the SPA
 * can call them with the Firebase ID Token after Firebase auth succeeds.
 *
 * AI-Manual has only one user_type ('user'), so the user_type parameter from
 * Poranos's version is dropped.
 */
import axios from "axios";

import { getApiBaseUrl, fetchWithoutAuth } from "./fetchApi";

const API_BASE = getApiBaseUrl();

export async function postAuthWithToken(
  firebaseUser,
  endpoint = "/login/",
  displayName = null,
  maxRetries = 5,
) {
  const idToken = await firebaseUser.getIdToken(true);

  const data = { idToken };
  if (displayName !== null) {
    data.display_name = displayName;
  }

  let retries = 0;
  while (retries < maxRetries) {
    try {
      await axios.post(`${API_BASE}/auth${endpoint}`, data, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return firebaseUser;
    } catch (error) {
      if (error.response?.status === 401) {
        // backend が意図的に拒否した認証エラーはリトライしても無駄なので即座に投げる。
        // （トークン伝播待ちの一時的な 401 だけをリトライ対象にする）
        const code = error.response?.data?.code;
        if (code === "not_invited" || code === "account_disabled") {
          const rejected = new Error(
            error.response?.data?.detail || "サインインが拒否されました",
          );
          rejected.authCode = code;
          throw rejected;
        }
        retries++;
        console.warn(
          `⚠️ 401 on ${endpoint} (${retries}/${maxRetries}); retry in 1s…`,
        );
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else {
        throw error;
      }
    }
  }

  throw new Error(`Django auth failed after ${maxRetries} retries: ${endpoint}`);
}

/**
 * ログイン画面が出すボタンを backend に問い合わせる。
 * 取得に失敗しても画面が真っ白にならないよう、Google + Email の既定値に倒す。
 */
export async function fetchAuthProviders() {
  try {
    return await fetchWithoutAuth("/auth/providers/");
  } catch (error) {
    console.error("[authApi] providers fetch failed:", error);
    return {
      email_password: true,
      google: true,
      microsoft: false,
      okta: false,
      allow_open_signup: true,
    };
  }
}
